import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import {
  People,
  PersonAdd,
  MailOutline,
  AdminPanelSettings,
  Group,
  Security,
} from '@mui/icons-material';
import { userService } from '../services/userService';
import { groupService } from '../services/groupService';
import { roleService } from '../services/roleService';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  licensedUsers: number;
  guestUsers: number;
  sharedMailboxes: number;
  globalAdmins: number;
  securityGroups: number;
  microsoft365Groups: number;
}

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    licensedUsers: 0,
    guestUsers: 0,
    sharedMailboxes: 0,
    globalAdmins: 0,
    securityGroups: 0,
    microsoft365Groups: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);

      // Fetch all users, groups, and global admins
      const [usersResponse, groupsResponse, globalAdminsResponse] = await Promise.all([
        userService.getUsers({ top: 999 }),
        groupService.getGroups({ top: 999 }),
        roleService.getGlobalAdministrators(),
      ]);

      const users = (usersResponse as any).Data?.Value || (usersResponse as any).data?.value || [];
      const groups =
        (groupsResponse as any).Data?.Value || (groupsResponse as any).data?.value || [];
      const globalAdminMembers =
        (globalAdminsResponse as any).Data?.Value ||
        (globalAdminsResponse as any).data?.value ||
        [];

      // Calculate user statistics - handle both PascalCase and camelCase
      const licensedUsers = users.filter((u: any) => {
        const assignedLicenses = u.assignedLicenses || u.AssignedLicenses || [];
        return assignedLicenses.length > 0;
      }).length;

      const guestUsers = users.filter((u: any) => {
        const userType = u.userType || u.UserType || '';
        const userPrincipalName = u.userPrincipalName || u.UserPrincipalName || '';
        return userType === 'Guest' || userPrincipalName.includes('#EXT#');
      }).length;

      // Shared mailboxes typically don't have licenses and have specific RecipientTypeDetails
      const sharedMailboxes = users.filter((u: any) => {
        const assignedLicenses = u.assignedLicenses || u.AssignedLicenses || [];
        const mail = u.mail || u.Mail;
        const userType = u.userType || u.UserType || '';
        const userPrincipalName = u.userPrincipalName || u.UserPrincipalName || '';
        return (
          assignedLicenses.length === 0 &&
          mail &&
          userType !== 'Guest' &&
          !userPrincipalName.includes('#EXT#')
        );
      }).length;

      // Global admins - now using actual directory role membership
      const globalAdmins = globalAdminMembers.length;

      // Calculate group statistics - handle both PascalCase and camelCase
      const securityGroups = groups.filter((g: any) => {
        const securityEnabled = g.securityEnabled || g.SecurityEnabled;
        const groupTypes = g.groupTypes || g.GroupTypes || [];
        return securityEnabled && !groupTypes.includes('Unified');
      }).length;

      const microsoft365Groups = groups.filter((g: any) => {
        const groupTypes = g.groupTypes || g.GroupTypes || [];
        return groupTypes.includes('Unified');
      }).length;

      setStats({
        licensedUsers,
        guestUsers,
        sharedMailboxes,
        globalAdmins,
        securityGroups,
        microsoft365Groups,
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: number;
    color: string;
    onClick?: () => void;
  }> = ({ icon, title, value, color, onClick }) => (
    <Paper
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ fontSize: 40, mr: 2, color }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h4">{loading ? <CircularProgress size={24} /> : value}</Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Microsoft 365
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<People />}
            title="Licensed Users"
            value={stats.licensedUsers}
            color="primary.main"
            onClick={() => navigate('/users?filter=licensed')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<PersonAdd />}
            title="Guest Users"
            value={stats.guestUsers}
            color="info.main"
            onClick={() => navigate('/users?filter=guest')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MailOutline />}
            title="Shared Mailboxes"
            value={stats.sharedMailboxes}
            color="warning.main"
            onClick={() => navigate('/users?filter=mailbox')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<AdminPanelSettings />}
            title="Global Admins"
            value={stats.globalAdmins}
            color="error.main"
            onClick={() => navigate('/users?filter=admin')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<Security />}
            title="Security Groups"
            value={stats.securityGroups}
            color="secondary.main"
            onClick={() => navigate('/groups?filter=security')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<Group />}
            title="Microsoft 365 Groups"
            value={stats.microsoft365Groups}
            color="success.main"
            onClick={() => navigate('/groups?filter=microsoft365')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
